/**
 * La ficha — `OD-0020` E-01/E-02, nivel 2.
 *
 * Entra en el shard de UNA empresa, así que **queda registrado** antes de leer
 * nada y el cliente puede verlo (`D-04`). Se le dice a quien la abre: un rastro
 * que el operador no sabe que deja no disuade de nada.
 *
 * Lo que enseña es lo que hace falta para operar: si el obligado está
 * confirmado, si la autorización está viva y **qué lleva sin salir hacia la
 * AEAT**. Ni una factura, ni un importe, ni un dato de ningún cliente final
 * (E-01) — y eso lo garantiza la API, no esta pantalla.
 */
import { useEffect, useState } from "react";
import { api, type EmpresaFicha } from "../api/cliente";
import { t } from "../textos";
import { CorreosDeEmpresa } from "./Correos";

/**
 * Una fecha ISO como la lee una persona; si no es fecha, se deja tal cual.
 *
 * CON SEGUNDOS, y no es un capricho: lo único que se mira en esta tabla es si
 * dos envíos del mismo obligado respetaron el tiempo de espera del art. 16.2
 * —60 segundos al principio, y el que la AEAT diga después—. Al minuto, dos
 * envíos a las 09:26:10 y 09:26:50 se ven iguales y no se puede comprobar
 * nada.
 */
function fechaLegible(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
}

export function Empresa({ id, onVolver }: { id: string; onVolver: () => void }) {
  const [estado, setEstado] = useState<"cargando" | "ok" | "no-encontrado" | "sin-mfa" | "error">("cargando");
  const [ficha, setFicha] = useState<EmpresaFicha | null>(null);
  const [subsanando, setSubsanando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");
  const [motivoEstado, setMotivoEstado] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<{ readonly tono: "exito" | "error" | "info"; readonly texto: string } | null>(null);

  useEffect(() => {
    void (async (): Promise<void> => {
      setEstado("cargando");
      const r = await api.empresa(id);
      if (r.ok) {
        setFicha(r.datos);
        setEstado("ok");
        return;
      }
      if (r.estado === 404 || r.estado === 401) setEstado("no-encontrado");
      else if (r.estado === 403) setEstado("sin-mfa");
      else setEstado("error");
    })();
  }, [id]);

  /**
   * Cerrar o reabrir. El motivo es obligatorio y lo escribe una persona: un
   * «¿seguro?» se pulsa sin leer, y dentro de un año el motivo es lo único
   * que explicará por qué esta empresa dejó de operar.
   */
  async function cambiarEstado(accion: "cerrar" | "reabrir"): Promise<void> {
    setAviso(null);
    const razon = motivoEstado.trim();
    if (razon === "") {
      setAviso({ tono: "error", texto: t.empresa.motivoFalta });
      return;
    }
    setOcupado(true);
    const r = accion === "cerrar" ? await api.cerrar(id, razon) : await api.reabrir(id, razon);
    setOcupado(false);
    if (!r.ok) {
      setAviso({ tono: "error", texto: t.comun.error });
      return;
    }
    setMotivoEstado("");
    setAviso({ tono: "exito", texto: accion === "cerrar" ? t.empresa.cerrada : t.empresa.reabierta });
    const f = await api.empresa(id);
    if (f.ok) setFicha(f.datos);
  }

  /**
   * Empuja la cola de esta empresa. El resultado se cuenta tal cual viene: si
   * la AEAT pidió esperar, se dice que se espera. Fingir que se envió sería
   * peor que no tener el botón.
   */
  /**
   * Repasa los archivos de esta empresa — `OD-0044` D-06.
   *
   * El resultado se cuenta TAL CUAL, como «enviar ahora»: «nada que comprobar»
   * no es «todo correcto», y un archivo que no se pudo leer no es un archivo
   * corrupto. Fingir que fue bien sería peor que no tener el botón.
   *
   * Y recarga la ficha: la tabla de marcados de arriba tiene que reflejar lo
   * que acaba de pasar, no lo que había al abrir la pantalla.
   */
  async function comprobarArchivos(): Promise<void> {
    setAviso(null);
    setOcupado(true);
    const r = await api.comprobarArchivos(id);
    setOcupado(false);
    if (!r.ok) {
      setAviso({
        tono: "error",
        texto: r.error?.motivo === "NO_OBJECT_STORE" ? t.empresa.repasoSinAlmacen : t.comun.error,
      });
      return;
    }
    const s = r.datos;
    if (!s.habiaAlgo) {
      setAviso({ tono: "info", texto: t.empresa.repasoVacio });
    } else if (s.marcados > 0) {
      setAviso({ tono: "error", texto: t.empresa.repasoMal(s.verificados, s.marcados) });
    } else if (s.sinDeterminar > 0) {
      /*
        `info` y no `error`: el almacen no respondio, y eso no dice NADA de los
        archivos. Pintarlo en rojo haria pensar que estan corruptos, que es
        justo la confusion que OD-0044 D-02 existe para impedir. El texto dice
        lo que paso y que se reintentan.
      */
      setAviso({ tono: "info", texto: t.empresa.repasoDudoso(s.sinDeterminar) });
    } else {
      setAviso({ tono: "exito", texto: t.empresa.repasoBien(s.verificados) });
    }

    // La tabla de marcados de arriba tiene que reflejar lo que ACABA de pasar,
    // no lo que habia al abrir la pantalla.
    const refrescada = await api.empresa(id);
    if (refrescada.ok) setFicha(refrescada.datos);
  }

  async function enviarAhora(): Promise<void> {
    setAviso(null);
    setOcupado(true);
    const r = await api.enviarAhora(id);
    setOcupado(false);
    if (!r.ok) {
      setAviso({
        tono: "error",
        texto: r.error?.motivo === "SUBMISSION_NOT_CONFIGURED" ? t.empresa.envioSinConfigurar : t.comun.error,
      });
      return;
    }
    const e = r.datos;
    // Los contadores primero: lo que se le dice a la persona depende de si
    // QUEDA cola, no solo de lo que devolvio esta llamada.
    const f = await api.empresa(id);
    if (f.ok) setFicha(f.datos);
    const quedan = f.ok ? (f.datos.remision?.pending ?? 0) : 0;

    if (e.rejected > 0) setAviso({ tono: "error", texto: t.empresa.envioRechazado(e.rejected) });
    else if (e.sent > 0) setAviso({ tono: "exito", texto: t.empresa.envioHecho(e.sent) });
    else if (e.waiting > 0) setAviso({ tono: "info", texto: t.empresa.envioEsperando });
    // Nada enviado y nada esperando, pero la cola NO esta vacia: el intento
    // anterior fallo y el reintento va a una hora vista. Decir "no habia nada"
    // seria el mensaje comodo y falso.
    else if (quedan > 0) setAviso({ tono: "info", texto: t.empresa.envioNoTocaba });
    else setAviso({ tono: "exito", texto: t.empresa.envioNada });
  }

  async function subsanar(recordId: string): Promise<void> {
    setAviso(null);
    setOcupado(true);
    const r = await api.subsanar(id, recordId, motivo.trim());
    setOcupado(false);
    if (!r.ok) {
      setAviso({ tono: "error", texto: t.comun.error });
      return;
    }
    setSubsanando(null);
    setMotivo("");
    setAviso({ tono: "exito", texto: t.empresa.subsanada });
    // Se recarga: el registro nuevo cambia lo que queda por subsanar.
    const f = await api.empresa(id);
    if (f.ok) setFicha(f.datos);
  }

  const volver = (
    <button type="button" data-variante="secundario" onClick={onVolver}>
      {t.comun.volver}
    </button>
  );

  if (estado === "cargando") return <p className="suave">{t.comun.cargando}</p>;
  if (estado === "no-encontrado")
    return (
      <>
        <p>{t.comun.noEncontrado}</p>
        {volver}
      </>
    );
  if (estado === "sin-mfa")
    return (
      <>
        <p className="aviso" data-tono="aviso">{t.comun.sinMfa}</p>
        {volver}
      </>
    );
  if (estado === "error" || ficha === null)
    return (
      <>
        <p className="aviso" data-tono="error">{t.comun.error}</p>
        {volver}
      </>
    );

  return (
    <>
      <h1>{t.empresa.titulo}</h1>
      <p className="aviso" data-tono="info">{t.empresa.registrado}</p>
      {aviso !== null ? (
        <p className="aviso" data-tono={aviso.tono} role="status">
          {aviso.texto}
        </p>
      ) : null}

      <div className="tarjeta">
        <p>
          <strong>{ficha.name === "" ? t.empresas.desconocida : ficha.name}</strong>
        </p>
        <p className="suave">{ficha.id}</p>
        <table>
          <tbody>
            <tr>
              <th>{t.empresas.columnas.plan}</th>
              <td>{ficha.planVersion}</td>
            </tr>
            <tr>
              <th>{t.empresas.columnas.estado}</th>
              <td>{ficha.status}</td>
            </tr>
            <tr>
              <th>{t.empresas.columnas.shard}</th>
              <td>{ficha.shard ?? ""}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>{t.empresa.emisor}</h2>
      {ficha.issuer === null ? (
        <p className="suave">{t.empresa.sinEmisor}</p>
      ) : (
        <table>
          <tbody>
            <tr>
              <th>{t.empresa.nombre}</th>
              <td>{ficha.issuer.legalName}</td>
            </tr>
            <tr>
              <th>{t.empresa.nif}</th>
              <td>{ficha.issuer.taxId}</td>
            </tr>
            <tr>
              <th>{t.empresa.confirmado}</th>
              <td>{ficha.issuer.confirmed ? t.comun.si : t.comun.no}</td>
            </tr>
          </tbody>
        </table>
      )}

      <h2>{t.empresa.representacion}</h2>
      <p>{ficha.representation}</p>

      <h2>{t.empresa.remision}</h2>
      {ficha.remision === null ? (
        <p className="suave">{t.empresa.sinRemision}</p>
      ) : (
        <table>
          <tbody>
            <tr>
              <th>{t.empresa.pendientes}</th>
              <td className="numero">{ficha.remision.pending}</td>
            </tr>
            <tr>
              <th>{t.empresa.rechazados}</th>
              <td className="numero">{ficha.remision.rejected}</td>
            </tr>
            <tr>
              <th>{t.empresa.incidencia}</th>
              <td>{ficha.remision.incident ? t.comun.si : t.comun.no}</td>
            </tr>
          </tbody>
        </table>
      )}

      {/*
        El boton existe porque el cron corre una vez al dia, y "hasta manana"
        no es una respuesta cuando alguien pregunta por su factura. No se
        ensena si no hay nada pendiente: un boton que no hace nada engana.
      */}
      {ficha.remision !== null && ficha.remision.pending > 0 ? (
        <p>
          <button type="button" onClick={() => void enviarAhora()} disabled={ocupado}>
            {ocupado ? t.empresa.enviando : t.empresa.enviarAhora}
          </button>
          <br />
          <span className="suave">{t.empresa.enviarAhoraDetalle}</span>
        </p>
      ) : null}

      {/*
        Los ultimos envios. Sin esto, "hay una incidencia abierta" es un
        callejon sin salida: el panel sabe que algo fallo y no puede decir que.
      */}
      <h2>{t.empresa.envios}</h2>
      {ficha.submissions.length === 0 ? (
        <p className="suave">{t.empresa.enviosVacio}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t.empresa.enviosColumnas.cuando}</th>
              <th>{t.empresa.enviosColumnas.estado}</th>
              <th>{t.empresa.enviosColumnas.registros}</th>
              <th>{t.empresa.enviosColumnas.csv}</th>
            </tr>
          </thead>
          <tbody>
            {ficha.submissions.map((e) => (
              <tr key={e.sentAt}>
                <td>{fechaLegible(e.sentAt)}</td>
                <td>
                  {e.failure !== null
                    ? t.empresa.envioFallo(e.failure)
                    : (e.envelopeState ?? t.empresa.envioSinRespuesta)}
                </td>
                <td className="numero">{e.recordCount}</td>
                <td>{e.csv ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/*
        OD-0066. Sale de GLOBAL_DB y no del shard: lo que la ficha anota es la
        entrada en el shard, no esta lista.
      */}
      <CorreosDeEmpresa organizationId={id} />

      {/*
        OD-0044 D-04. Va en la ficha y no en el listado porque entrar en el shard
        de un cliente deja rastro obligatorio (OD-0020 D-04), y aqui ya se ha
        dejado. Para "¿me esta pasando en varias?", el recuento agregado sale de
        la linea de registro del cron diario.
      */}
      <h2>{t.empresa.archivos}</h2>
      <p className="suave">{t.empresa.archivosDetalle}</p>
      <button type="button" onClick={() => void comprobarArchivos()} disabled={ocupado}>
        {ocupado ? t.empresa.comprobando : t.empresa.comprobarArchivos}
      </button>
      {ficha.archivosMarcados.length === 0 ? (
        <p className="suave">{t.empresa.archivosVacio}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t.empresa.archivosColumnas.archivo}</th>
              <th>{t.empresa.archivosColumnas.estado}</th>
              <th>{t.empresa.archivosColumnas.comprobado}</th>
            </tr>
          </thead>
          <tbody>
            {ficha.archivosMarcados.map((a) => (
              <tr key={a.fileId}>
                <td>
                  {a.originalName}
                  <div className="suave">{a.fileType}</div>
                </td>
                <td>{a.estado === "NO_ESTA" ? t.empresa.archivoNoEsta : t.empresa.archivoHashDistinto}</td>
                <td>{a.verifiedAt ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/*
        Parte 4 §4: subsanar es un registro NUEVO con el mismo IDFactura, no una
        edicion. Por eso pide un motivo escrito antes de dejar pulsar: dentro de
        un año, eso es lo unico que explicara los dos registros.
      */}
      <h2>{t.empresa.defectos}</h2>
      <p className="suave">{t.empresa.defectosDetalle}</p>
      {ficha.defects.length === 0 ? (
        <p className="suave">{t.empresa.defectosVacio}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t.empresa.defectosColumnas.numero}</th>
              <th>{t.empresa.defectosColumnas.estado}</th>
              <th>{t.empresa.defectosColumnas.error}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ficha.defects.map((d) => (
              <tr key={d.recordId}>
                <td>
                  {d.numSerie}
                  <div className="suave">{d.isSubsanation ? t.empresa.esSubsanacion : d.fechaExpedicion}</div>
                </td>
                <td>
                  {d.status === "REJECTED"
                    ? t.empresa.rechazado
                    : d.status === "RECONCILIATION_REQUIRED"
                      ? t.empresa.duplicado
                      : t.empresa.conErrores}
                  {d.status === "RECONCILIATION_REQUIRED" ? (
                    <div className="suave">{t.empresa.duplicadoDetalle(d.aeatState)}</div>
                  ) : null}
                </td>
                <td>
                  {d.errorCode ?? ""}
                  <div className="suave">{d.errorDescription ?? ""}</div>
                </td>
                <td>
                  {/* OD-0012 §31.2: un duplicado se reconcilia, no se subsana. */}
                  {d.status === "RECONCILIATION_REQUIRED" ? null : subsanando === d.recordId ? (
                    <>
                      <label htmlFor={`motivo-${d.recordId}`}>
                        <span>{t.empresa.subsanarMotivo}</span>
                        <input id={`motivo-${d.recordId}`} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                      </label>
                      <p className="suave">{t.empresa.subsanarMotivoAyuda}</p>
                      <button type="button" onClick={() => void subsanar(d.recordId)} disabled={ocupado || motivo.trim() === ""}>
                        {ocupado ? t.empresa.subsanando : t.empresa.subsanarConfirmar}
                      </button>{" "}
                      <button type="button" data-variante="secundario" onClick={() => setSubsanando(null)}>
                        {t.apoderamientos.cancelar}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      data-variante="secundario"
                      onClick={() => {
                        setSubsanando(d.recordId);
                        setMotivo("");
                      }}
                      disabled={ocupado}
                    >
                      {t.empresa.subsanar}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/*
        Cerrar y reabrir: la API las tiene desde OD-0020 E-05 y el panel no las
        ofrecia, asi que el backoffice sabia contar lo que pasaba y no podia
        hacer nada al respecto.
      */}
      <h2>{t.empresa.cerrarTitulo}</h2>
      <p className="suave">{t.empresa.cerrarExplicacion}</p>
      {ficha.status === "CLOSED" ? <p className="aviso" data-tono="aviso">{t.empresa.estadoCerrada}</p> : null}
      <p>
        <label htmlFor="motivo-estado">{t.empresa.motivo}</label>
        <br />
        <textarea
          id="motivo-estado"
          rows={2}
          maxLength={500}
          value={motivoEstado}
          onChange={(e) => setMotivoEstado(e.target.value)}
        />
      </p>
      <p>
        <button
          type="button"
          onClick={() => void cambiarEstado(ficha.status === "CLOSED" ? "reabrir" : "cerrar")}
          disabled={ocupado}
        >
          {ficha.status === "CLOSED" ? t.empresa.reabrir : t.empresa.cerrar}
        </button>
      </p>

      <h2>{t.empresa.borrar}</h2>
      <p className="suave">{t.empresa.borrarExplicacion}</p>
      <p className="suave">{t.empresa.sinFacturas}</p>
      {volver}
    </>
  );
}
